import React from "react";
import { useT } from "../../lib/i18n";
import { Empty } from "./OverviewTab";
import RuleEngineTab from "./RuleEngineTab";

function normalizeClass(value) {
  const normalized = String(value || "").replace(/\s+/g, "").replace(/^(CLASS|CLASE)/i, "").toUpperCase();
  if (/^I(S|M|R)?$/.test(normalized)) return "I";
  return normalized;
}

export default function ClassificationTab({ version, device, onEditAssessment }) {
  const t = useT();
  if (!version) return <Empty msg={t("ov.no_analysis")} />;
  const c = version.classification || {};
  const assessedClass = normalizeClass(c.suggested_class);
  const masterClass = normalizeClass(device?.mdr_class);
  const classMismatch = Boolean(assessedClass && masterClass && assessedClass !== masterClass);
  return (
    <div className="space-y-4">
      <div className="grid-card p-6 flex items-center justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-[var(--text-2)]">{t("classification.suggested")}</div>
          <div className="mt-2 flex items-center gap-3">
            <span className={`chip chip-${c.suggested_class || "I"} text-base px-3 py-1`} data-testid="classification-class">{c.suggested_class || "—"}</span>
            <span className="font-mono text-sm text-[var(--text-2)]">{t("classification.completeness")} {Math.round(((c.input_completeness ?? c.confidence) || 0) * 100)}%</span>
          </div>
          <p className="mt-2 max-w-3xl text-xs leading-relaxed text-[var(--text-2)]">{t("classification.preliminary_note")}</p>
        </div>
      </div>

      <details className="rounded-sm border border-[var(--border)] p-4" data-testid="classification-scope-note">
        <summary className="cursor-pointer text-sm font-semibold text-[var(--accent-text)]">{t("classification.scope_title")}</summary>
        <p className="mt-3 text-sm leading-relaxed text-[var(--text-2)]">{t("classification.scope_note")}</p>
        <a href="https://eur-lex.europa.eu/eli/reg_impl/2022/2347/oj" target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-[var(--accent-text)] underline">{t("classification.scope_source")}</a>
      </details>

      {c.needs_human_review && c.review_items?.length > 0 && (
        <div className="grid-card border-l-4 border-l-amber-500 p-5" role="status" data-testid="classification-review-items">
          <div className="mb-2 text-sm font-semibold text-[var(--risk-warning)]">{t("classification.needs_review")}</div>
          <ul className="list-disc space-y-1 pl-5 text-sm text-[var(--text-2)]">
            {c.review_items.map((item, index) => <li key={`${index}-${item}`}>{item}</li>)}
          </ul>
          {onEditAssessment ? (
            <button
              type="button"
              onClick={onEditAssessment}
              className="mt-4 rounded-sm border border-[var(--border-bright)] bg-[var(--surface)] px-3 py-2 text-sm font-semibold text-[var(--accent-text)] hover:bg-[var(--surface-hover)]"
              data-testid="classification-edit-answers"
            >
              {t("classification.review_answers")}
            </button>
          ) : null}
        </div>
      )}

      {classMismatch && (
        <div className="grid-card border-l-4 border-l-amber-500 p-5" role="alert" data-testid="classification-device-mismatch">
          <div className="text-sm font-semibold text-[var(--risk-warning)]">{t("classification.device_mismatch_title")}</div>
          <p className="mt-1 text-sm leading-relaxed text-[var(--text-2)]">
            {t("classification.device_mismatch_detail")
              .replace("{device}", device.name || t("wiz.f.device_name"))
              .replace("{master}", device.mdr_class)
              .replace("{assessment}", c.suggested_class)}
          </p>
        </div>
      )}

      {c.escalations?.length > 0 && (
        <div className="grid-card p-5 border-l-4 border-l-amber-500">
          <div className="text-xs font-mono uppercase tracking-wider text-[var(--risk-warning)] mb-2">{t("classification.escalations")}</div>
          <ul className="text-sm space-y-1 text-[var(--text)]">
            {c.escalations.map((e) => <li key={e}>· {e}</li>)}
          </ul>
        </div>
      )}

      <RuleEngineTab version={version} />
    </div>
  );
}
